import React, { useState, useEffect } from 'react';
import {
  VStack, Input, Textarea, Text, Box, Button, Switch, FormControl, FormLabel,
  useToast, InputGroup, InputRightElement, SimpleGrid
} from '@chakra-ui/react';
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
import SelectorPerfilUsuario from 'components/PerfilUsuario/SelectorPerfilUsuario';
import { GOOGLE_MAPS_API_KEY } from 'config/googleMaps';

type Modo = 'new' | 'edit' | 'view';

interface Props {
  modo: Modo;
  data?: any;
  onSubmit?: (data: any) => void;
}

const RegistroMultaObstruccion: React.FC<Props> = ({ modo, data, onSubmit }) => {
  const toast = useToast();
  const [material, setMaterial] = useState(data?.material || '');
  const [area, setArea] = useState(data?.area || '');
  const [dias, setDias] = useState(data?.dias || '');
  const [retiro, setRetiro] = useState(data?.retiro || false);
  const [costo, setCosto] = useState(data?.costo || 0);
  const [direccion, setDireccion] = useState(data?.direccion || '');
  const [lat, setLat] = useState(Number(data?.lat) || 19.4326);
  const [lng, setLng] = useState(Number(data?.lng) || -99.1332);
  const [inspector, setInspector] = useState(data?.inspector || '');
  const [observaciones, setObservaciones] = useState(data?.observaciones || '');
  const [perfilSeleccionado, setPerfilSeleccionado] = useState<any>(null);

  const handlePerfil = (perfil: any) => {
      setPerfilSeleccionado(perfil);
    };
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries: ['places'],
  });

  useEffect(() => {
    setCosto(area * dias * 100); // Dummy tarifa
  }, [area, dias]);

  const buscarDireccion = () => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: direccion }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const loc = results[0].geometry.location;
        setLat(loc.lat());
        setLng(loc.lng());
        setDireccion(results[0].formatted_address);
      } else {
        toast({
          title: 'Dirección no encontrada',
          description: 'Especifica una dirección más precisa.',
          status: 'error',
        });
      }
    });
  };

  const handleGuardar = async () => {
    const payload = {
      material,
      area,
      dias,
      retiro,
      costo,
      direccion,
      lat,
      lng,
      inspector,
      observaciones,
      fechaRegistro: new Date()
    };

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || "http://127.0.0.1:5000"}/api/obras/multa-obstruccion`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Error al registrar la multa.');

      toast({
        title: 'Éxito',
        description: 'Multa registrada correctamente.',
        status: 'success',
      });

      onSubmit?.(payload);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Hubo un problema al registrar la multa.',
        status: 'error',
      });
    }
  };

  if (!isLoaded) return <Text>Cargando mapa...</Text>;

  return (
    <VStack spacing={4} align="stretch">
      {(modo === 'edit' || modo === 'new') ? (
        <>
        <SelectorPerfilUsuario onPerfilSeleccionado={handlePerfil} />
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4} mb={4}>
        
          <Input placeholder="Material" value={material} onChange={(e) => setMaterial(e.target.value)} />
          <Input type="number" placeholder="Área m²" value={area} onChange={(e) => setArea(Number(e.target.value))} />
          <Input type="number" placeholder="Días" value={dias} onChange={(e) => setDias(Number(e.target.value))} />
          <FormControl display="flex" alignItems="center">
            <FormLabel htmlFor="retiro" mb="0">¿Retiro forzoso?</FormLabel>
            <Switch id="retiro" isChecked={retiro} onChange={(e) => setRetiro(e.target.checked)} />
          </FormControl>
          </SimpleGrid>
          <Text><strong>Costo estimado:</strong> ${costo.toFixed(2)}</Text>

          <InputGroup>
            <Input placeholder="Dirección" value={direccion} onChange={(e) => setDireccion(e.target.value)} />
            <InputRightElement width="4.5rem">
              <Button h="1.75rem" size="sm" onClick={buscarDireccion}>Buscar</Button>
            </InputRightElement>
          </InputGroup>

          <Box h="200px" w="100%">
            <GoogleMap
              zoom={15}
              center={{ lat, lng }}
              mapContainerStyle={{ width: '100%', height: '100%' }}
            >
              <Marker
                position={{ lat, lng }}
                draggable
                onDragEnd={(e) => {
                  setLat(e.latLng?.lat() || lat);
                  setLng(e.latLng?.lng() || lng);
                }}
              />
            </GoogleMap>
          </Box>

          <Input placeholder="Inspector" value={inspector} onChange={(e) => setInspector(e.target.value)} />
          <Textarea placeholder="Observaciones" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />

          <Button colorScheme="green" onClick={handleGuardar}>
            {modo === 'new' ? 'Registrar' : 'Actualizar'}
          </Button>
        </>
      ) : (
        <>
          <SimpleGrid columns={2} spacing={4}>
            <Box>
              <Text><strong>Material:</strong> {material}</Text>
              <Text><strong>Área:</strong> {area} m²</Text>
              <Text><strong>Días:</strong> {dias}</Text>
              <Text><strong>Retiro:</strong> {retiro ? 'Sí' : 'No'}</Text>
              <Text><strong>Costo:</strong> ${costo}</Text>
            </Box>
            <Box>
              <Text><strong>Dirección:</strong> {direccion}</Text>
              <Text><strong>Inspector:</strong> {inspector}</Text>
              {observaciones && <Text><strong>Observaciones:</strong> {observaciones}</Text>}
            </Box>
          </SimpleGrid>
          <Box h="200px" w="100%">
            <GoogleMap
              zoom={15}
              center={{ lat, lng }}
              mapContainerStyle={{ width: '100%', height: '100%' }}
            >
              <Marker position={{ lat, lng }} />
            </GoogleMap>
          </Box>
        </>
      )}
    </VStack>
  );
};

export default RegistroMultaObstruccion;
